import { useState } from "react";
import { useTranslation } from "react-i18next";

import { faPlus, faTrash } from "@fortawesome/pro-regular-svg-icons";

import { TextField } from "@mui/material";

import { Icon } from "@/components";
import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";

import {
	useCreateSubTaskTemplateMutation,
	useDeleteSubTaskTemplateMutation,
	useGetTaskTemplateByIdQuery,
} from "../../../redux/task-template/task-template.api";
import { SubTaskTemplateInfo } from "../schema/task-template";
import { SubTaskTemplateModal } from "./SubTaskTemplateModal";

const fieldSx = {
	"& .MuiInputBase-root": {
		borderRadius: "12px",
		fontSize: "14px",
	},
	"& .MuiOutlinedInput-notchedOutline": {
		borderColor: "#DCDAD6",
	},
};

interface SubTaskTemplateListProps {
	taskTemplateId: number;
}

export const SubTaskTemplateList = ({ taskTemplateId }: SubTaskTemplateListProps) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`manage-task-templates.${key}`);

	const { data: template } = useGetTaskTemplateByIdQuery(taskTemplateId);
	const subTaskTemplates = template?.subTaskTemplates || [];

	const [createSubTask] = useCreateSubTaskTemplateMutation();
	const [deleteSubTask] = useDeleteSubTaskTemplateMutation();

	const [newTitle, setNewTitle] = useState("");
	const [editingSubTask, setEditingSubTask] = useState<SubTaskTemplateInfo | null>(null);

	const handleAdd = async () => {
		if (!newTitle.trim()) return;

		await createSubTask({ taskTemplateId, title: newTitle.trim() });
		setNewTitle("");
	};

	const handleDelete = async (sub: SubTaskTemplateInfo) => {
		if (!sub.id) return;

		await deleteSubTask({ taskTemplateId, id: sub.id });
	};

	return (
		<div className="mt-2 pt-5 border-t border-gray-200">
			<p className="text-[14px] font-bold leading-5 text-primary-blue-dark mb-3">
				{ts("form.subtasks")}
			</p>

			{subTaskTemplates.length > 0 && (
				<div className="space-y-2 mb-3">
					{subTaskTemplates.map((sub) => (
						<div
							key={sub.id}
							className="flex items-center gap-2 rounded-xl bg-gray-50 p-3 cursor-pointer hover:bg-gray-100 transition-colors"
							onClick={() => setEditingSubTask(sub)}
						>
							<div className="grow min-w-0">
								<p className="text-[14px] font-medium text-primary-blue-dark truncate">
									{sub.title}
								</p>
								{sub.description && (
									<p className="text-[12px] text-gray-500 truncate mt-0.5">{sub.description}</p>
								)}
							</div>
							{sub.priority && (
								<span className="text-[12px] text-gray-500 whitespace-nowrap">{sub.priority}</span>
							)}
							<Button
								color={ButtonColor.ACTION_SECONDARY}
								image={<Icon icon={faTrash} />}
								size={ButtonSize.XS}
								title=""
								type="button"
								onClick={(e) => {
									e.stopPropagation();
									handleDelete(sub);
								}}
							/>
						</div>
					))}
				</div>
			)}

			<div className="flex items-center gap-2">
				<TextField
					fullWidth
					inputProps={{ style: { padding: "10px 14px", fontSize: "14px" } }}
					placeholder={ts("form.new-subtask-placeholder")}
					size="small"
					sx={fieldSx}
					value={newTitle}
					onChange={(e) => setNewTitle(e.target.value)}
					onKeyDown={(e) => {
						if (e.key === "Enter") {
							e.preventDefault();
							handleAdd();
						}
					}}
				/>
				<Button
					color={ButtonColor.BRAND}
					image={<Icon icon={faPlus} />}
					size={ButtonSize.XS}
					title=""
					type="button"
					onClick={handleAdd}
				/>
			</div>
			<SubTaskTemplateModal
				isOpen={!!editingSubTask}
				subTask={editingSubTask}
				taskTemplateId={taskTemplateId}
				onClose={() => setEditingSubTask(null)}
			/>
		</div>
	);
};
